import React, { useState, useRef, useEffect } from "react";
import { FiBell, FiChevronDown, FiMapPin, FiGrid } from "react-icons/fi";
import { useSite } from "../../context/SiteContext";
import { useRole } from "../../context/RoleContext";
import { ALL_ROLES, VIEW_BADGE, ROLE_VIEW, DEMO_USERS } from "../../context/RoleContext";
import { showOrgSelector } from "../../utils/permissions";
import "./TopNavBar.css";

// Health dot color helper
const healthDot = (score) =>
  score >= 80 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";

// Status label color
const statusColor = (status) =>
  status === "Active" ? "#10b981" : status === "Trial" ? "#f59e0b" : "#ef4444";

const TopNavBar = () => {
  const {
    selectedOrgId,  setSelectedOrgId,
    selectedSiteId, setSelectedSiteId,
    selectedOrg, selectedSite,
    visibleSites, organizations,
  } = useSite();

  const { currentRole, setCurrentRole, currentView, currentUser } = useRole();

  const [orgOpen,  setOrgOpen]  = useState(false);
  const [siteOpen, setSiteOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);

  const orgRef  = useRef(null);
  const siteRef = useRef(null);
  const userRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (orgRef.current  && !orgRef.current.contains(e.target))  setOrgOpen(false);
      if (siteRef.current && !siteRef.current.contains(e.target)) setSiteOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setRoleOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Total unacknowledged alerts across visible sites
  const alertCount = visibleSites.reduce((sum, s) => {
    if (selectedSiteId !== "all" && s.id !== selectedSiteId) return sum;
    return sum + (s.alerts || []).filter(a => a.status === "Active").length;
  }, 0);

  const viewBadge = VIEW_BADGE[currentView] || VIEW_BADGE.site;
  const orgSelectorVisible = showOrgSelector(currentRole);

  return (
    <div className="tnb-navbar">
      {/* Brand mark */}
      <div className="tnb-brand">
        <div className="tnb-brand-logo">
          <div className="tnb-brand-dot" />
        </div>
        <span className="tnb-brand-name">IoTMATE</span>
      </div>

      <div className="tnb-sep" />

      {/* Organisation Selector — visible only for roles with showOrgSelector */}
      {orgSelectorVisible && (
        <>
          <div className="tnb-selector" ref={orgRef}>
            <FiGrid size={13} className="tnb-sel-icon" />
            <span className="tnb-sel-label">Org</span>
            <div
              className={`tnb-dropdown-trigger ${orgOpen ? "tnb-trigger-open" : ""}`}
              onClick={() => { setOrgOpen(o => !o); setSiteOpen(false); }}
            >
              <span className="tnb-sel-value">
                {selectedOrg ? selectedOrg.name : "All Organizations"}
              </span>
              <FiChevronDown size={13} className={`tnb-chevron ${orgOpen ? "tnb-chevron-open" : ""}`} />
            </div>

            {orgOpen && (
              <div className="tnb-dropdown">
                <div
                  className={`tnb-dd-item ${selectedOrgId === "all" ? "tnb-dd-item-active" : ""}`}
                  onClick={() => { setSelectedOrgId("all"); setOrgOpen(false); }}
                >
                  <span className="tnb-dd-label">All Organizations</span>
                  <span className="tnb-dd-count">{organizations.length} orgs</span>
                </div>
                {organizations.map(o => (
                  <div
                    key={o.id}
                    className={`tnb-dd-item ${selectedOrgId === o.id ? "tnb-dd-item-active" : ""}`}
                    onClick={() => { setSelectedOrgId(o.id); setOrgOpen(false); }}
                  >
                    <div className="tnb-dd-label">{o.name}</div>
                    <div className="tnb-dd-meta">
                      <span className="tnb-dd-type">{o.type}</span>
                      <span className="tnb-dd-count">{o.siteIds.length} site{o.siteIds.length !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="tnb-sep" />
        </>
      )}

      {/* Site Selector */}
      <div className="tnb-selector" ref={siteRef}>
        <FiMapPin size={13} className="tnb-sel-icon" />
        <span className="tnb-sel-label">Site</span>
        <div
          className={`tnb-dropdown-trigger ${siteOpen ? "tnb-trigger-open" : ""}`}
          onClick={() => { setSiteOpen(s => !s); setOrgOpen(false); }}
        >
          {selectedSite ? (
            <>
              <span
                className="tnb-site-health-dot"
                style={{ background: healthDot(selectedSite.healthScore) }}
              />
              <span className="tnb-sel-value">{selectedSite.name}</span>
            </>
          ) : (
            <span className="tnb-sel-value">All Sites</span>
          )}
          <FiChevronDown size={13} className={`tnb-chevron ${siteOpen ? "tnb-chevron-open" : ""}`} />
        </div>

        {siteOpen && (
          <div className="tnb-dropdown tnb-dropdown-wide">
            <div
              className={`tnb-dd-item ${selectedSiteId === "all" ? "tnb-dd-item-active" : ""}`}
              onClick={() => { setSelectedSiteId("all"); setSiteOpen(false); }}
            >
              <span className="tnb-dd-label">All Sites</span>
              <span className="tnb-dd-count">{visibleSites.length} sites</span>
            </div>
            {visibleSites.map(s => (
              <div
                key={s.id}
                className={`tnb-dd-item ${selectedSiteId === s.id ? "tnb-dd-item-active" : ""}`}
                onClick={() => { setSelectedSiteId(s.id); setSiteOpen(false); }}
              >
                <span
                  className="tnb-site-health-dot"
                  style={{ background: healthDot(s.healthScore) }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tnb-dd-label" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</div>
                  <div className="tnb-dd-meta">
                    <span className="tnb-dd-type">{s.type}</span>
                    <span
                      className="tnb-dd-status"
                      style={{ color: statusColor(s.status) }}
                    >
                      {s.status}
                    </span>
                  </div>
                </div>
                {(s.alerts || []).filter(a => a.status === "Active").length > 0 && (
                  <span className="tnb-dd-alert-dot">
                    {(s.alerts || []).filter(a => a.status === "Active").length}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Context breadcrumb pill */}
      {(selectedOrg || selectedSite) && (
        <div className="tnb-context-pill">
          {selectedOrg && <span>{selectedOrg.name}</span>}
          {selectedOrg && selectedSite && <span className="tnb-context-sep">›</span>}
          {selectedSite && <span>{selectedSite.name}</span>}
        </div>
      )}

      {/* View Badge */}
      <div
        className="tnb-view-badge"
        style={{
          background: `${viewBadge.color}22`,
          color: viewBadge.color,
          border: `1px solid ${viewBadge.color}55`,
        }}
      >
        {viewBadge.label}
      </div>

      {/* Alert Bell */}
      <div className="tnb-bell-wrap">
        <FiBell size={17} />
        {alertCount > 0 && (
          <span className="tnb-bell-badge">{alertCount > 9 ? "9+" : alertCount}</span>
        )}
      </div>

      {/* User + Role Switcher */}
      <div className="tnb-user-wrap" ref={userRef}>
        <div className="tnb-user" onClick={() => setRoleOpen(o => !o)}>
          <div className="tnb-user-avatar">
            {currentUser.initials}
          </div>
          <div className="tnb-user-info">
            <div className="tnb-user-name">{currentUser.name}</div>
            <div className="tnb-user-role">{currentRole}</div>
          </div>
          <FiChevronDown
            size={12}
            className={`tnb-chevron ${roleOpen ? "tnb-chevron-open" : ""}`}
            style={{ color: "rgba(255,255,255,0.4)", marginLeft: 2 }}
          />
        </div>

        {roleOpen && (
          <div className="tnb-role-dropdown">
            <div className="tnb-role-header">Switch Role (Demo)</div>
            {ALL_ROLES.map(role => {
              const view = ROLE_VIEW[role];
              const badge = VIEW_BADGE[view];
              const user = DEMO_USERS[role];
              return (
                <div
                  key={role}
                  className={`tnb-role-item ${currentRole === role ? "tnb-role-item-active" : ""}`}
                  onClick={() => { setCurrentRole(role); setRoleOpen(false); }}
                >
                  <span className="tnb-role-view-dot" style={{ background: badge.color }} />
                  <span className="tnb-role-name">{role}</span>
                  <span className="tnb-role-user">{user.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopNavBar;
